import { useQuery } from '@tanstack/react-query';

import { getEngagementChecklist } from '@/api/onboardingChecklist';
import type { EngagementOut } from '@/api/types';
import { OnboardingChecklist } from '@/components/OnboardingChecklist';
import { useAuth } from '@/auth/AuthContext';

/** Detailed onboarding status on the customer page: the full derived
 *  checklist card per active engagement — the same payload the portal
 *  and the engagement view render. The engagements table shows the
 *  compact done/total bar; this is the drill-down without leaving the
 *  customer. */
export function CustomerOnboardingOverview({ engagements }: { engagements: EngagementOut[] }) {
  if (engagements.length === 0) return null;
  return (
    <section className="grid gap-3">
      <h3 className="text-base font-semibold text-brand-navy">Onboarding</h3>
      {engagements.map((engagement) => (
        <EngagementOnboardingCard key={engagement.id} engagement={engagement} />
      ))}
    </section>
  );
}

function EngagementOnboardingCard({ engagement }: { engagement: EngagementOut }) {
  const { userKey, getIdToken } = useAuth();
  const checklist = useQuery({
    queryKey: ['engagementChecklist', engagement.id, userKey],
    queryFn: async () => getEngagementChecklist(engagement.id, (await getIdToken()) ?? ''),
  });
  if (checklist.isPending) {
    return <p className="text-sm text-brand-slate">Loading onboarding for {engagement.name}…</p>;
  }
  // A failed refetch keeps rendering the cached card (the next
  // successful refetch heals it); the error line is only for the
  // initial fetch failing with nothing to show.
  if (checklist.data == null) {
    return (
      <p className="text-sm text-destructive">
        Couldn&apos;t load onboarding for {engagement.name}.
      </p>
    );
  }
  return (
    <OnboardingChecklist
      data={checklist.data}
      title={engagement.name}
      description="Derived from what the customer has submitted — identical to their portal view."
    />
  );
}
